import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";

export interface RequeueTodayQuote {
  id: string;
  vehicleRegistration: string | null;
  postcode: string | null;
  step: number;
  contactName: string;
  contactPhone: string;
  createdDate: string;
}

export interface FailedLead {
  id: string;
  vehicleRegistration: string | null;
  contactName: string;
  contactPhone: string;
  sendAttempts: number;
  createdDate: string;
  updatedDate: string;
  quoteId: string | null;
  customerId: string | null;
  lastSendError: string | null;
  retriesUsedUp: boolean;
  wilmaProConfigured: boolean;
}

export interface Step8Quote {
  id: string;
  vehicleRegistration: string | null;
  postcode: string | null;
  step: number;
  furthestStep: number;
  status: number;
  contactName: string;
  contactPhone: string;
  createdDate: string;
  updatedDate: string;
}

export interface QueuedQuote {
  id: string;
  vehicleRegistration: string | null;
  postcode: string | null;
  step: number;
  contactName: string;
  contactPhone: string;
  enqueuedAtUtc: string;
  attempt: number;
}

export interface QueueStatus {
  approximateCount: number;
  quotes: QueuedQuote[];
}

export interface VrmTraceQuote {
  id: string;
  createdDate: string;
  updatedDate: string | null;
  status: number;
  step: number;
  furthestStep: number;
  origin: string;
  sourceCustomerId: string | null;
  sourceWebsiteDomain: string | null;
  collectedCustomerId: string | null;
  priceCollected: number;
  dropOffCustomerId: string | null;
  priceDropOff: number;
  amendedPrice: number | null;
  assignedCustomerId: string | null;
  superchatStatus: number;
  superchatSentOn: string | null;
  superchatAutomation: string | null;
  pricesEmailStatus: number;
  pricesEmailSentOn: string | null;
  handedToCallCentreOn: string | null;
  convertedToLeadOn: string | null;
  visibleToSav: boolean;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  hasAdClick: boolean;
  utmParams: string | null;
}

export interface VrmTraceMessage {
  timestamp: string;
  status: number;
  recipient: string | null;
}

export interface VrmTraceLead {
  id: string;
  quoteId: string | null;
  createdDate: string;
  updatedDate: string | null;
  deletedDate: string | null;
  customerId: string | null;
  ownedBySav: boolean;
  status: number;
  website: number;
  isCollection: boolean;
  price: number;
  commission: number;
  collectionCustomerId: string | null;
  dropOffCustomerId: string | null;
  routingOutcome: number;
  routingReason: string | null;
  duplicateOfLeadId: string | null;
  duplicateOfCustomerId: string | null;
  reallocated: boolean;
  reallocationNote: string | null;
  failedToSend: boolean;
  sendAttempts: number;
  lastSendError: string | null;
  messages: VrmTraceMessage[];
}

export interface VrmTrace {
  vrm: string;
  quotes: VrmTraceQuote[];
  leads: VrmTraceLead[];
}

export function useVrmTrace(vrm: string | null) {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: ["functions-trace-vrm", vrm],
    queryFn: () => get<DefaultResponseWithData<VrmTrace>>(`/functions/trace-vrm/${encodeURIComponent(vrm!)}`),
    enabled: !!vrm,
  });
}

export default function useFunctions() {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();

  const queueStatus = useQuery({
    queryKey: ["functions-queue-status"],
    queryFn: () => get<DefaultResponseWithData<QueueStatus>>("/functions/queue"),
    refetchInterval: 30_000,
  });

  const requeuePreview = useQuery({
    queryKey: ["functions-requeue-preview"],
    queryFn: () => get<DefaultResponseWithData<RequeueTodayQuote[]>>("/functions/requeue-today"),
    enabled: false,
  });

  const failedLeads = useQuery({
    queryKey: ["functions-failed-leads"],
    queryFn: () => get<DefaultResponseWithData<FailedLead[]>>("/functions/failed-leads"),
  });

  const step8Quotes = useQuery({
    queryKey: ["functions-step8-quotes"],
    queryFn: () => get<DefaultResponseWithData<Step8Quote[]>>("/functions/step8-without-leads"),
  });

  const requeueToday = useMutation(
    (quoteIds: string[]) => post<{ quoteIds: string[] }, DefaultResponseWithData<number>>("/functions/requeue-today", { quoteIds }),
  );

  const resendLead = useMutation(
    (leadId: string) => post<{}, DefaultResponseWithData<boolean>>(`/functions/failed-leads/${leadId}/resend`, {}),
    {
      // On failure too, so the row picks up the new attempt count and error.
      onSettled: () => {
        queryClient.invalidateQueries(["functions-failed-leads"]);
      },
    }
  );

  return {
    queueStatus,
    requeuePreview,
    failedLeads,
    step8Quotes,
    requeueToday,
    resendLead,
  };
}
